import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const inputVariants = cva(
  "ui-flex ui-w-full ui-rounded-[10px] ui-bg-white ui-text-zinc-800 ui-transition-colors ui-outline-none",
  {
    variants: {
      variant: {
        outline:
          "input-outline [&:hover:not(:focus)]:input-outline-hover active:input-outline-active focus:input-outline-focus focus:!ui-bg-white \
          data-[disabled=true]:input-outline-disabled data-[disabled=true]:ui-bg-zinc-50 data-[disabled=true]:ui-opacity-50 data-[error=true]:input-outline-error",
        filled:
          "ui-bg-zinc-400/10 hover:input-filled-hover hover:ui-bg-zinc-400/15 active:input-filled-active focus:input-filled-focus \
          data-[disabled=true]:input-outline-disabled data-[disabled=true]:ui-bg-zinc-400/10 data-[disabled=true]:ui-opacity-60 \
          data-[error=true]:input-outline-error data-[focused=true]:ui-bg-white",
        standard:
          "ui-h-[47px] !ui-rounded-none ui-border-0 ui-border-b-1 ui-border-black-10 hover:ui-border-black-20 \
          focus:ui-border-primary-dark focus:ui-border-b-1 focus:ui-shadow-[0_2px_0px_0px_color-mix(in_srgb,var(--flo-primary-dark-color)_20%,transparent)] \
          ui-transition-all ui-duration-200 \
          data-[disabled=true]:ui-border-gray-light data-[disabled=true]:ui-opacity-50 \
          data-[error=true]:ui-border-red-dark data-[error=true]:ui-shadow-[0_2px_0px_0px_color-mix(in_srgb,var(--flo-red-dark-color)_20%,transparent)]",
      },
      dimension: {
        sm: "ui-h-7 ui-text-text-xs-regular ui-rounded-[8px] ui-px-3",
        md: "ui-h-9 ui-text-text-sm-regular ui-rounded-[8px] ui-px-3 ui-py-1",
        lg: "ui-h-11 ui-text-text-sm-regular ui-px-[10px] ui-py-2 ui-rounded-[10px]",
      },
    },
    defaultVariants: {
      variant: "outline",
      dimension: "lg",
    },
  }
);

const divAdornmentVariants = cva(
  "ui-flex ui-items-center ui-bg-white ui-justify-center data-[disabled=true]:ui-cursor-not-allowed data-[disabled=true]:ui-opacity-50",
  {
    variants: {
      variant: {
        outline:
          "input-outline [&:hover:not(:focus-within)]:input-outline-hover active:input-outline-active \
          focus-within:input-outline-focus data-[disabled=true]:input-outline-disabled data-[disabled=true]:ui-bg-zinc-50 \
          data-[error=true]:input-outline-error",
        filled:
          "ui-bg-zinc-400/10 hover:input-filled-hover hover:ui-bg-zinc-400/15 active:input-filled-active active:!ui-bg-white focus-within:input-filled-focus focus-within:!ui-bg-white \
          data-[disabled=true]:input-outline-disabled data-[disabled=true]:ui-bg-zinc-50 \
          data-[error=true]:input-outline-error",
        standard:
          "!ui-rounded-none ui-border-0 ui-border-b ui-border-gray-dark hover:ui-border-gray-medium \
          focus-within:ui-border-primary-dark focus-within:ui-border-b-2 ui-transition-all ui-duration-200 \
          data-[disabled=true]:ui-border-gray-light data-[error=true]:ui-border-red-dark",
      },
      dimension: {
        sm: "ui-h-7 ui-text-text-xs-regular ui-rounded-[8px] ui-px-2",
        md: "ui-h-9 ui-text-text-sm-regular ui-rounded-[8px] ui-px-2 ui-py-1",
        lg: "ui-h-11 ui-text-text-sm-regular ui-px-[10px] ui-py-2 ui-rounded-[10px]",
      },
    },
    defaultVariants: {
      variant: "outline",
      dimension: "lg",
    },
  }
);

const inputVariantsOnAdornments = cva(
  "ui-flex ui-items-center ui-justify-center ui-w-full ui-rounded-[10px] ui-bg-transparent ui-transition-colors ui-outline-none",
  {
    variants: {
      variant: {
        outline: "disabled:ui-bg-zinc-50",
        filled: "",
        standard: "",
      },
      dimension: {
        sm: "ui-h-7 ui-text-text-xs-regular ui-px-2",
        md: "ui-h-9 ui-text-text-sm-regular ui-px-2 ui-py-1",
        lg: "ui-h-11 ui-text-text-sm-regular ui-px-2.5 ui-py-2",
      },
    },
    defaultVariants: {
      variant: "outline",
      dimension: "lg",
    },
  }
);

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
  VariantProps<typeof inputVariants> {
  startAdornment?: JSX.Element;
  endAdornment?: JSX.Element;
  error?: boolean;
  label?: string;
  required?: boolean;
  maxSize?: number;
  wrapperStyle?: React.CSSProperties;
  showMaxSize?:boolean
  /** Fired when the user tries to type/paste past `maxSize`. The keystroke is still blocked. */
  onLimitExceeded?: () => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      dimension,
      variant,
      startAdornment,
      endAdornment,
      value,
      defaultValue,
      label,
      required,
      maxSize,
      wrapperStyle,
      showMaxSize=true,
      onLimitExceeded,
      ...props
    },
    ref
  ) => {
    // Separate out style so it stays on the input element and doesn't bleed into the wrapper.
    const { style, ...restProps } = props as React.HTMLAttributes<HTMLInputElement> & Record<string, any>;
    const [focused, setFocused] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState(
      value ?? defaultValue ?? ""
    );
    const [charCount, setCharCount] = React.useState(
      typeof value === "string" ? value.length : typeof defaultValue === "string" ? defaultValue.length : 0
    );
    const hasAdornment = Boolean(startAdornment) || Boolean(endAdornment);

    // Handle controlled and uncontrolled inputs
    const inputValue = value !== undefined ? value : internalValue;

    // Sync charCount with value prop changes
    React.useEffect(() => {
      if (value !== undefined) {
        setCharCount(typeof value === "string" ? value.length : typeof value === "number" ? String(value).length : 0);
      }
    }, [value]);

    // `maxLength` swallows the keystroke before any change event fires, so the attempt has to be
    // detected on keydown/paste for callers that want to react to it.
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const target = e.currentTarget;
      const isPrintable = e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey;
      const hasSelection = target.selectionStart !== target.selectionEnd;
      if (maxSize && isPrintable && !hasSelection && target.value.length >= maxSize) {
        onLimitExceeded?.();
      }
      props.onKeyDown?.(e);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const target = e.currentTarget;
      if (maxSize) {
        const selected = (target.selectionEnd ?? 0) - (target.selectionStart ?? 0);
        const pasted = e.clipboardData.getData("text");
        if (target.value.length - selected + pasted.length > maxSize) onLimitExceeded?.();
      }
      props.onPaste?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (maxSize && e.target.value.length > maxSize) {
        onLimitExceeded?.();
        return;
      }
      if (value === undefined) {
        setInternalValue(e.target.value);
      }
      setCharCount(e.target.value.length);
      props.onChange?.(e);
    };

    const inputElement = hasAdornment ? (
      <div
        className={cn(
          divAdornmentVariants({ variant, dimension }),
          className
        )}
        data-disabled={Boolean(props.disabled)}
        data-focused={focused}
        data-error={Boolean(props.error)}
        style={wrapperStyle}
      >
        {startAdornment && (
          <div className={cn("text-muted-foreground")}>{startAdornment}</div>
        )}
        <input
          className={cn(
            inputVariantsOnAdornments({ variant, dimension }),
            className
          )}
          type={type}
          ref={ref}
          value={inputValue}
          maxLength={maxSize}
          onFocus={(e) => {
            setFocused(true);
            restProps.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            restProps.onBlur?.(e);
          }}
          onChange={handleChange}
          style={style as React.CSSProperties}
          {...restProps}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
        />
        {endAdornment && (
          <div className={cn("text-muted-foreground")}>{endAdornment}</div>
        )}
      </div>
    ) : (
      <input
        className={cn(inputVariants({ variant, dimension, className }))}
        type={type}
        ref={ref}
        value={inputValue}
        maxLength={maxSize}
        data-disabled={Boolean(props.disabled)}
        data-error={Boolean(props.error)}
        data-focused={focused}
        onFocus={(e) => {
          setFocused(true);
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          props.onBlur?.(e);
        }}
        onChange={handleChange}
        {...props}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />
    );

    // If label or maxSize is provided, wrap with container
    if (label || maxSize) {
      return (
        <div className="ui-flex ui-flex-col ui-w-full ui-gap-0.5">
          <div className="ui-flex ui-justify-between ui-px-0.5">
            {label && (
              <label className="ui-text-text-xs-medium ui-text-zinc-500">
                {label}
                {required && <span className="ui-text-red-500"> *</span>}
              </label>
            )}
            {maxSize && showMaxSize && (
              <div className="ui-flex ui-justify-end ui-text-text-xs-regular ui-text-zinc-500">
                {charCount} / {maxSize}
              </div>
            )}
          </div>
          {inputElement}
        </div>
      );
    }

    return inputElement;
  }
);

Input.displayName = "Input";

export { Input };
